"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Phone, Menu, X, ChevronDown, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function TopBar() {
  return <div data-section-id="22578" className="bg-primary text-white h-[40px] w-full relative z-50">
      <div className="container mx-auto max-w-[1200px] h-full px-4 flex items-center justify-between">
        {}
        <Link to="tel:+18005550199" className="flex items-center gap-2 text-sm font-medium hover:text-secondary transition-colors duration-200 group">
          <Phone className="w-4 h-4 text-secondary fill-current" />
          <span className="hidden sm:inline">(310) 858-7373</span>
        </Link>

        {}
        <Button asChild className="bg-secondary hover:bg-secondary/90 text-white h-7 px-4 text-xs font-bold uppercase tracking-wide rounded-lg border-none shadow-none">
          <Link to="#">
            <SafeHydrate><Calendar className="w-3 h-3 mr-1.5" /></SafeHydrate>
            Book Free Consultation
          </Link>
        </Button>
      </div>
    </div>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-6 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex" viewport={true}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-primary font-medium text-[14px] hover:text-secondary focus:text-secondary transition-colors h-auto py-2 px-3 rounded-none border-b-2 border-transparent hover:border-secondary data-[state=open]:border-secondary">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-white rounded-xl shadow-xl border-t-2 border-secondary">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-secondary focus:bg-accent focus:text-secondary">
                            <div className="text-sm font-semibold leading-none text-primary group-hover:text-secondary">
                              {child.label}
                            </div>
                            <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                              Modern solutions for restorative sleep.
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group inline-flex h-auto w-max items-center justify-center bg-transparent px-3 py-2 text-[14px] font-medium text-primary transition-colors hover:text-secondary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent hover:border-secondary">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  return <div className="lg:hidden">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)} className="text-primary hover:text-secondary hover:bg-transparent" aria-label="Open menu">
        <Menu className="h-6 w-6" />
      </Button>

      {}
      <div className={cn("fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm transition-opacity duration-300", isOpen ? "opacity-100" : "opacity-0 pointer-events-none")} onClick={() => setIsOpen(false)} />

      {}
      <div className={cn("fixed top-0 right-0 z-[101] h-full w-[300px] bg-white shadow-2xl transition-transform duration-300 ease-in-out transform flex flex-col", isOpen ? "translate-x-0" : "translate-x-full")}>
        <div className="p-5 flex items-center justify-between border-b border-secondary/10">
          <span className="font-bold text-lg text-primary">Menu</span>
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-secondary">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-5">
          {loading ? <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-6 w-full bg-muted rounded animate-pulse" />)}
            </div> : <nav className="flex flex-col space-y-1">
              {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-gray-50 last:border-0">
                  {item.children && item.children.length > 0 ? <div>
                      <Button variant="ghost" onClick={() => toggleExpand(item.id)} className="flex w-full items-center justify-between py-6 px-0 text-base font-medium text-primary hover:text-secondary hover:bg-transparent transition-colors h-auto">
                        {item.label}
                        <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", expandedItems.includes(item.id) ? "rotate-180" : "")} />
                      </Button>
                      <div className={cn("overflow-hidden transition-all duration-300 ease-in-out", expandedItems.includes(item.id) ? "max-h-[500px] opacity-100 mb-3" : "max-h-0 opacity-0")}>
                        <div className="pl-4 flex flex-col space-y-2 border-l-2 border-secondary/20 ml-1">
                          {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="block py-1 text-sm text-muted-foreground hover:text-secondary" onClick={() => setIsOpen(false)}>
                              {child.label}
                            </Link>)}
                        </div>
                      </div>
                    </div> : <Link to={item.href || "#"} className="block py-3 text-base font-medium text-primary hover:text-secondary transition-colors" onClick={() => setIsOpen(false)}>
                      {item.label}
                    </Link>}
                </div>)}
            </nav>}
        </div>

        <div className="p-5 border-t border-secondary/10 bg-accent">
          <Button className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold" asChild>
            <Link to="#" className="flex items-center justify-center w-full h-full">
              Book Free Consultation
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function ClinicHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="main-navigation" className="w-full flex flex-col font-sans bg-background text-foreground">
      <TopBar />
      
      <header className={cn("sticky top-0 z-40 w-full transition-all duration-300 border-b", isScrolled ? "bg-white/80 backdrop-blur-md border-secondary/10 shadow-md py-2" : "bg-white border-[hsl(221,83%,95%)] py-4")}>
        <div className="container mx-auto max-w-[1200px] px-4 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex-shrink-0 mr-8">
            <img src="https://asleepdentist.com/wp-content/uploads/2026/04/523944_1670889061_Color-scaled.png" alt="A Sleep Dentist" className="h-12 w-auto object-contain" />
          </Link>

          {}
          <MenuProvider menu_id="38" className="flex-1 flex justify-end">
            <DesktopNav />
            <MobileNav />
          </MenuProvider>
        </div>
      </header>
    </section>;
}